import { z } from 'zod';

// The manual communication channels an agent can record on a contact, lead or
// deal. ONE source for all three surfaces (IMM-913): the lead, contact and
// shared-activity vocabularies were three identical copies of the same list,
// and teaching only one leaves a surface unable to write what the other two
// can read.
export const MANUAL_COMMUNICATION_TYPES = ['CALL', 'SMS', 'WHATSAPP', 'FAX', 'LETTER', 'EMAIL'] as const;

export const ManualCommunicationTypeSchema = z.enum(MANUAL_COMMUNICATION_TYPES);

export type ManualCommunicationType = z.infer<typeof ManualCommunicationTypeSchema>;

// „Betreff" belongs to the types that carry one; „Dauer" only to a call. The
// membership tests are exported next to the enum and consumed by every layer
// that encodes them — three domain drafts and the composer — rather than being
// re-typed at four call sites (BL-049).
export const COMMUNICATION_TYPES_WITH_SUBJECT: ReadonlyArray<ManualCommunicationType> = ['FAX', 'LETTER', 'EMAIL'];

export function communicationCarriesSubject(type: ManualCommunicationType): boolean {
  return COMMUNICATION_TYPES_WITH_SUBJECT.includes(type);
}

export const COMMUNICATION_TYPE_WITH_DURATION: ManualCommunicationType = 'CALL';

export function communicationCarriesDuration(type: ManualCommunicationType): boolean {
  return type === COMMUNICATION_TYPE_WITH_DURATION;
}

// The Ergebnis vocabulary. `LEAD_ACTIVITY_OUTCOMES` (leads.schemas.ts) and
// `COMMUNICATION_OUTCOMES` (activity-records.schemas.ts) are the same four
// tokens declared twice; both become aliases of this, so no import site
// changes and the narrowing below cannot be written against only one of them.
export const COMMUNICATION_OUTCOME_TOKENS = ['REACHED', 'NOT_REACHED', 'VOICEMAIL', 'CALLBACK_AGREED'] as const;

export type CommunicationOutcomeToken = (typeof COMMUNICATION_OUTCOME_TOKENS)[number];

// „Antwort erhalten" / „Keine Antwort" — a NARROWING of the shipped outcomes,
// not a new vocabulary: an email has no voicemail to reach and no callback to
// agree, so those two tokens are simply not offered and are refused if sent.
export const EMAIL_COMMUNICATION_OUTCOMES = ['REACHED', 'NOT_REACHED'] as const;

/**
 * The Ergebnis vocabulary a communication type offers. Total over
 * `MANUAL_COMMUNICATION_TYPES` by construction — the ONE place the narrowing
 * lives, so the select that offers the options and the domain that refuses
 * the others can never disagree about which they are.
 */
export function communicationOutcomesFor(type: ManualCommunicationType): ReadonlyArray<CommunicationOutcomeToken> {
  return type === 'EMAIL' ? EMAIL_COMMUNICATION_OUTCOMES : COMMUNICATION_OUTCOME_TOKENS;
}

export function communicationAcceptsOutcome(type: ManualCommunicationType, outcome: string): boolean {
  return (communicationOutcomesFor(type) as ReadonlyArray<string>).includes(outcome);
}

// A projection body carries `communicationType` as a bare string (a row
// written before the store's CHECK can carry anything), so the read side
// needs a guard before it can ask for a type's vocabulary.
export function isManualCommunicationType(value: string): value is ManualCommunicationType {
  return (MANUAL_COMMUNICATION_TYPES as ReadonlyArray<string>).includes(value);
}
